import { STORAGE_KEYS } from '@/constants/storage';
import { TASK_QUERY_KEYS } from '@/hooks/task-query-keys';
import { readAuthSessionClaims } from '@/lib/auth-session-claims';

import { getOfflineDb, getOfflineDbAtWipeEpoch } from './connection';
import {
  clearEntrySessionComplete,
  isEntrySessionComplete,
  isGateEnabled,
  isReadAllowed,
  markEntrySessionComplete,
} from './meta';
import { captureNativeBootstrapSessionWriteGeneration } from './bootstrap-cache-writer';
import { readOfflineListCache } from './list-cache';
import { getBootstrapProfile, listOrgMemberships } from './repositories/core';
import {
  TASKS_OFFLINE_SCOPE,
  TASKS_OFFLINE_VIEW_SIGNATURE,
  compareCachedTasks,
  isCachedTaskListAcceptable,
} from './task-cache-contract';
import { isNativePlatform } from '../platform';

import type { QueryClient } from '@tanstack/react-query';
import type { Session } from '@supabase/supabase-js';
import type {
  OrganizationMember,
  OrganizationMemberWithProfile,
} from '@/contexts/organization-types';
import type { TaskWithRawAssignees } from '@/hooks/use-tasks-helpers';
import type { CachedUserProfile } from './bootstrap-cache-writer';

export {
  __resetNativeBootstrapWriteGenerationForTests,
  activateNativeBootstrapWriteGeneration,
  beginNativeBootstrapMembershipProof,
  cacheNativeBootstrapMemberships,
  cacheNativeBootstrapProfile,
  captureNativeBootstrapSessionWriteGeneration,
  captureNativeBootstrapMembershipWriteGeneration,
  commitNativeBootstrapMembershipProof,
  deactivateNativeBootstrapWriteGeneration,
} from './bootstrap-cache-writer';
export type {
  CachedUserProfile,
  NativeBootstrapMembershipProofGeneration,
  NativeBootstrapMembershipWriteGeneration,
  NativeBootstrapWriteGeneration,
} from './bootstrap-cache-writer';

export interface NativeOfflineBootstrap {
  readonly profile: CachedUserProfile | null;
  readonly memberships: OrganizationMember[];
  readonly selectedOrganizationId: string;
  readonly currentMembers: OrganizationMemberWithProfile[];
  readonly tasks: TaskWithRawAssignees[] | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function parseProfile(payload: string | null, userId: string): CachedUserProfile | null {
  if (!payload) {
    return null;
  }
  try {
    const value = JSON.parse(payload) as unknown;
    if (!isRecord(value) || value.id !== userId) {
      return null;
    }
    return value as unknown as CachedUserProfile;
  } catch {
    return null;
  }
}

function parseMembership(payload: string, userId: string): OrganizationMember | null {
  try {
    const value = JSON.parse(payload) as unknown;
    if (
      !isRecord(value) ||
      value.user_id !== userId ||
      value.status !== 'active' ||
      typeof value.organization_id !== 'string' ||
      !isRecord(value.organization) ||
      value.organization.id !== value.organization_id ||
      typeof value.organization.name !== 'string'
    ) {
      return null;
    }
    return value as unknown as OrganizationMember;
  } catch {
    return null;
  }
}

function currentMemberFromBootstrap(
  membership: OrganizationMember,
  profile: CachedUserProfile | null,
  session: Session,
): OrganizationMemberWithProfile {
  const [firstName = '', ...lastNameParts] = profile?.full_name?.trim().split(/\s+/u) ?? [];
  return {
    id: membership.id,
    user_id: membership.user_id,
    organization_id: membership.organization_id,
    role: membership.role,
    status: membership.status,
    joined_at: membership.joined_at,
    created_at: membership.created_at,
    updated_at: membership.updated_at,
    first_name: firstName || null,
    last_name: lastNameParts.join(' ') || null,
    email: session.user.email ?? '',
    avatar_url: profile?.avatar_url ?? null,
  };
}

/** Persist a determinate app-entry decision for this stable auth session. */
export async function markNativeBootstrapSessionComplete(session: Session): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }
  const claims = readAuthSessionClaims(session.access_token);
  if (claims?.subject !== session.user.id || claims.sessionId === null) {
    return;
  }
  const generation = captureNativeBootstrapSessionWriteGeneration(
    session.user.id,
    claims.sessionId,
  );
  if (!generation) {
    return;
  }
  try {
    const cache = await getOfflineDbAtWipeEpoch(session.user.id, generation.wipeEpoch);
    const currentGeneration = captureNativeBootstrapSessionWriteGeneration(
      session.user.id,
      claims.sessionId,
    );
    if (cache && currentGeneration?.generation === generation.generation) {
      await markEntrySessionComplete(cache, claims.sessionId);
    }
  } catch {
    // Entry stays online-only if its disposable proof cannot be written.
  }
}

/** Remove reusable offline-entry proof before routing an exact session through MFA. */
export async function clearNativeBootstrapSessionComplete(
  session: Session | null,
): Promise<boolean> {
  if (!isNativePlatform()) {
    return true;
  }
  if (!session) {
    return false;
  }
  const claims = readAuthSessionClaims(session.access_token);
  if (claims?.subject !== session.user.id || claims.sessionId === null) {
    return false;
  }
  const generation = captureNativeBootstrapSessionWriteGeneration(
    session.user.id,
    claims.sessionId,
  );
  if (!generation) {
    return false;
  }
  try {
    const cache = await getOfflineDbAtWipeEpoch(session.user.id, generation.wipeEpoch);
    if (!cache) {
      return false;
    }
    await clearEntrySessionComplete(cache, claims.sessionId);
    return true;
  } catch {
    return false;
  }
}

/** Read reusable offline-entry proof only for the exact stable auth session. */
export async function hasNativeBootstrapSessionComplete(session: Session): Promise<boolean> {
  if (!isNativePlatform()) {
    return false;
  }
  const claims = readAuthSessionClaims(session.access_token);
  if (claims?.subject !== session.user.id || claims.sessionId === null) {
    return false;
  }
  const generation = captureNativeBootstrapSessionWriteGeneration(
    session.user.id,
    claims.sessionId,
  );
  if (!generation) {
    return false;
  }
  try {
    const cache = await getOfflineDbAtWipeEpoch(session.user.id, generation.wipeEpoch);
    if (!cache) {
      return false;
    }
    const hasCompletionProof = await isEntrySessionComplete(cache, claims.sessionId);
    const currentGeneration = captureNativeBootstrapSessionWriteGeneration(
      session.user.id,
      claims.sessionId,
    );
    return (
      hasCompletionProof &&
      currentGeneration?.generation === generation.generation &&
      currentGeneration.wipeEpoch === generation.wipeEpoch
    );
  } catch {
    return false;
  }
}

/**
 * Load the minimum verified state needed to route and paint native Home.
 * Payload access remains behind the database's R15 lock and per-org gate.
 */
export async function loadNativeOfflineBootstrap(
  session: Session,
  nowMs: number = Date.now(),
): Promise<NativeOfflineBootstrap | null> {
  if (!isNativePlatform()) {
    return null;
  }

  try {
    const userId = session.user.id;
    const claims = readAuthSessionClaims(session.access_token);
    if (claims?.subject !== userId || claims.sessionId === null) {
      return null;
    }
    const cache = await getOfflineDb(userId);
    if (!cache || !(await isReadAllowed(cache, nowMs))) {
      return null;
    }
    if (
      claims.assuranceLevel !== 'aal2' &&
      !(await isEntrySessionComplete(cache, claims.sessionId))
    ) {
      return null;
    }

    const rows = await listOrgMemberships(cache);
    const parsed = rows
      .map((row) => parseMembership(row.payload, userId))
      .filter((membership): membership is OrganizationMember => membership !== null);
    if (parsed.length === 0) {
      return null;
    }

    const memberships: OrganizationMember[] = [];
    for (const membership of parsed) {
      if (await isGateEnabled(cache, membership.organization_id)) {
        memberships.push(membership);
      }
    }
    if (memberships.length === 0) {
      return null;
    }

    const storedOrganizationId = globalThis.localStorage.getItem(
      STORAGE_KEYS.SELECTED_ORGANIZATION_ID,
    );
    const selectedMembership =
      memberships.find((membership) => membership.organization_id === storedOrganizationId) ??
      memberships[0];
    const profile = parseProfile(await getBootstrapProfile(cache, userId), userId);
    const tasks = await readOfflineListCache<TaskWithRawAssignees>({
      ownerUserId: userId,
      organizationId: selectedMembership.organization_id,
      table: 'tasks',
      scope: TASKS_OFFLINE_SCOPE,
      viewSignature: TASKS_OFFLINE_VIEW_SIGNATURE,
      shouldCache: isCachedTaskListAcceptable,
      compareCached: compareCachedTasks,
      nowMs,
    });

    return {
      profile,
      memberships,
      selectedOrganizationId: selectedMembership.organization_id,
      currentMembers: memberships.map((membership) =>
        currentMemberFromBootstrap(membership, profile, session),
      ),
      tasks,
    };
  } catch {
    return null;
  }
}

/** Seed stale data before observers mount so they paint first and reconcile second. */
export function primeNativeOfflineQueryCache(
  queryClient: QueryClient,
  session: Session,
  bootstrap: NativeOfflineBootstrap,
): void {
  const userId = session.user.id;
  const updatedAt = 0;
  queryClient.setQueryData(['profile', userId], bootstrap.profile, { updatedAt });
  queryClient.setQueryData(['user-organizations', userId], bootstrap.memberships, { updatedAt });
  for (const member of bootstrap.currentMembers) {
    queryClient.setQueryData(
      ['organization-members-cache', member.organization_id, userId],
      [member],
      { updatedAt },
    );
  }
  if (bootstrap.tasks !== null) {
    queryClient.setQueryData(
      [TASK_QUERY_KEYS.tasks, bootstrap.selectedOrganizationId, userId],
      bootstrap.tasks,
      { updatedAt },
    );
  }
}
